import React, { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { loadGoogleMaps } from '../../utils/googleMapsLoader';

/**
 * Google Places address autocomplete input.
 *
 * Why `google.maps.places.Autocomplete` (legacy class) instead of
 * `PlaceAutocompleteElement` (2024+ web component): the legacy class
 * integrates with controlled React inputs without wrapping the component
 * in a custom element. When the API surface stabilizes further we can
 * migrate to the element — until then this is the lowest-friction path.
 *
 * Props:
 *   value         — current input string (controlled)
 *   onChange      — (string) => void, fires on every keystroke
 *   onPlaceSelect — ({ address, zip_code, city, state, lat, lng }) => void
 *                   fires only when the user picks a suggestion
 *   placeholder, disabled, id, className — passed through
 */
export default function AddressAutocomplete({
    value = '',
    onChange,
    onPlaceSelect,
    placeholder = 'Start typing an address…',
    disabled = false,
    id,
    className = '',
}) {
    const inputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;

        loadGoogleMaps()
            .then((maps) => {
                if (cancelled) return;
                if (!maps) { setLoadFailed(true); return; }

                const ac = new maps.places.Autocomplete(inputRef.current, {
                    fields: ['formatted_address', 'address_components', 'geometry'],
                    types: ['address'],
                    componentRestrictions: { country: 'us' },
                });

                ac.addListener('place_changed', () => {
                    const place = ac.getPlace();
                    if (!place || !place.address_components) return;

                    const parsed = parsePlace(place);
                    onChange?.(parsed.address);
                    onPlaceSelect?.(parsed);
                });

                autocompleteRef.current = ac;
                setReady(true);
            })
            .catch((e) => {
                console.error('[AddressAutocomplete] Maps load failed', e);
                setLoadFailed(true);
            });

        return () => {
            cancelled = true;
            if (autocompleteRef.current && window.google?.maps?.event) {
                window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={`relative ${className}`}>
            <MapPin size={16} className="absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
            <input
                ref={inputRef}
                id={id}
                type="text"
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                placeholder={loadFailed ? 'Enter address manually' : placeholder}
                disabled={disabled}
                autoComplete="off"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
            />
            {!ready && !loadFailed && (
                <div className="absolute right-3 top-2.5 text-xs text-slate-400">loading…</div>
            )}
        </div>
    );
}

function parsePlace(place) {
    const out = {
        address: place.formatted_address || '',
        zip_code: '',
        city: '',
        state: '',
        lat: place.geometry?.location?.lat?.() ?? null,
        lng: place.geometry?.location?.lng?.() ?? null,
    };

    (place.address_components || []).forEach((c) => {
        if (c.types.includes('postal_code')) out.zip_code = c.long_name;
        else if (c.types.includes('locality')) out.city = c.long_name;
        else if (c.types.includes('administrative_area_level_1')) out.state = c.short_name;
    });

    return out;
}
